import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { useParams } from "react-router";
import { ClientPortalNavigation } from "../ClientPortalNavigation";
import "./clientPortalNewsletter.scss";
import CustomDropdown from "components/customDropdown/CustomDropdown";
import { IFacility } from "redux/reducers/models/facility";
import CustomDropdownOption from "components/customDropdown/CustomDropdownOption";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Spin from "components/spin/spin";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { faArrowLeft, faArrowRight, faStar } from "@fortawesome/pro-solid-svg-icons";
import { GetGuestNewsLetterPosts } from "api/rpc/2024-04/guest/newsletter/newsletter";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { ButtonNew as Button} from "components/buttonNew";
import { GetNewsLetterSubscriber, INewsLetterSubscriber, PostNewsLetterSubscriber, PutNewsLetterSubscriber } from "api/rpc/2024-04/customer/newsletter/newsletter";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import Popup from "components/popup/Popup";
import { TNewsLetterPost } from "api/rpc/2024-04/facilityAdmin/facility/newsletter";
import Markdown from "components/markdown/Markdown";
import { GetClient } from "api/rpc/2024-04/customer/client";
import { GetClientGuest } from "api/rpc/2024-04/guest/client";
import { GetFacility } from "api/rpc/2024-04/customer/facility";
import { GetFacilityGuest } from "api/rpc/2024-04/guest/facility";
import { sortFacilities } from "redux/reducers/clientPortal";
import { UserActive, UserLogout } from "api/rpc";
import { UserType } from "redux/reducers/models/user";
import { faCheck, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import moment from "moment";
import ReactDOM from "react-dom";
import MessageDisplay from "components/messageDisplay/MessageDisplay";

const TABLE_LIMIT = 5;

interface IState {
    facilitySearchQuery: string;
    searchedFacilities: Array<IFacility>;
  }

  interface IParams {
    clientShortName: string;
    facilityShortName: string;
  }

export default function ClientPortalNewsLetter() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { clientPortalStore } = useAppSelector(store => store);
    const [subscriber, setSubscriber] = useState<INewsLetterSubscriber>(undefined);
    const {state: subscribeModal, updateModal: updateSubscribeModal, closeModal: closeSubscribeModal} = useModal();
    const {state: unsubscribeModal, updateModal: updateUnsubscribeModal, closeModal: closeUnsubscribeModal} = useModal();
    const [posts, setPosts] = useState<Array<TNewsLetterPost>>(undefined);
    const [offset, setOffset] = useState<number>(0);
    const { clientShortName, facilityShortName } = useParams<IParams>();
    const [adminUserLoggedIn, setAdminUserLoggedIn] = useState(false);
    const windowSize = useWindowSize();
    const [selectedPost, setSelectedPost] = useState<TNewsLetterPost>(null);
    const urlParams = new URLSearchParams(window.location.search);
    const newsletterId = urlParams.get("newsletter_id");
    const [firstLoadComplete, setFirstLoadComplete] = useState(false);
 
    const [state, setState] = useState<IState>({
        facilitySearchQuery: "",
        searchedFacilities: [],
      });

    useEffect(() => {
        if (!clientPortalStore?.client) {
            void loadClient();
        } 
      }, []);

      useEffect(() => {
        const source = axios.CancelToken.source();
        if (clientPortalStore?.client && clientPortalStore?.selectedFacility) {
            void getNewsLetterPosts(source.token);
            if (clientPortalStore?.user) {
                void getNewsLetterSubscriber(source.token);
            }
        }
    
        return () => source.cancel();
      }, [clientPortalStore?.selectedFacility, offset]);

      async function loadClient() {
        let currentActiveUser = !!clientPortalStore?.user;
      let selectedClient = clientPortalStore?.client;
      let selectedFacility = clientPortalStore?.selectedFacility;

      const activeUserRes = await UserActive(false);
      if (activeUserRes?.status === StatusCode.OK) {
        currentActiveUser = true;
      }

      if (activeUserRes?.data?.user_type === UserType.FACILITY_ADMIN || activeUserRes?.data?.user_type === UserType.CLIENT_ADMIN) {
        setAdminUserLoggedIn(true);
        return;
      }

      if (
        !clientPortalStore?.client ||
        !clientPortalStore?.selectedFacility ||
        (clientPortalStore?.selectedFacility?.favourite === undefined && currentActiveUser)
      ) {
        let clientRes;
        let facilityRes;

        if (currentActiveUser) {
          currentActiveUser = true;
          clientRes = await GetClient({ short_name: clientShortName }, false);
        } else {
          clientRes = await GetClientGuest({ short_name: clientShortName }, false);
        }

        if (clientRes.status !== StatusCode.OK || clientRes?.data?.length === 0) {
          dispatch(showError(t("Error loading client")));
          return;
        }

        selectedClient = clientRes?.data[0];

        if (currentActiveUser) {
          facilityRes = await GetFacility({ client_id: selectedClient?.id, order_by: "long_name" }, false);
        } else {
          facilityRes = await GetFacilityGuest({ client_id: selectedClient?.id, order_by: "long_name" }, false);
        }

        if (facilityRes?.status !== StatusCode.OK || facilityRes?.data?.length === 0) {
          dispatch(showError(t("Error loading facilities")));
          return;
        }

        //Sort by Favourite facilities
        const sortedFacilities = sortFacilities(facilityRes?.data);

        selectedFacility = selectedFacility
          ? sortedFacilities?.find(facility => facility?.id === selectedFacility?.id)
          : facilityShortName
            ? sortedFacilities?.find(facility => facility?.short_name === facilityShortName)
            : sortedFacilities[0];

        if (!selectedFacility && sortedFacilities?.length > 0) {
          selectedFacility = sortedFacilities[0];
        }

        selectedClient = { ...selectedClient, facilities: sortedFacilities };

        dispatch(
          clientPortalUpdate({
            client: selectedClient,
            selectedFacility,
            user: currentActiveUser ? activeUserRes?.data : undefined,
          }),
        );
      } else {
        dispatch(
          clientPortalUpdate({
            user: currentActiveUser ? activeUserRes?.data : undefined,
          }),
        );
      }
      }

      async function getNewsLetterPosts(cancelToken?: CancelToken) {
        setPosts(undefined);
        const res = await GetGuestNewsLetterPosts({facility_id: clientPortalStore?.selectedFacility?.id, limit: TABLE_LIMIT, offset: offset}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setPosts([]);
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting newsletter")));
            return;
        }
        if (newsletterId && !firstLoadComplete) {
            const singleNewsletterRes = await GetGuestNewsLetterPosts({facility_id: clientPortalStore?.selectedFacility?.id, id: Number(newsletterId)}, false, cancelToken);
            if (singleNewsletterRes?.status === StatusCode.OK && singleNewsletterRes?.data?.length > 0) {
              ReactDOM.unstable_batchedUpdates(() => {
                setFirstLoadComplete(true);
                setSelectedPost(singleNewsletterRes?.data[0] as TNewsLetterPost);
              });
          }
        }
        setPosts(res?.data as Array<TNewsLetterPost>);
      }

      async function getNewsLetterSubscriber(cancelToken?: CancelToken) {
        setSubscriber(undefined);
        const res = await GetNewsLetterSubscriber({facility_id: clientPortalStore?.selectedFacility?.id}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setSubscriber(null);
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting newsletter subscriber")));
            return;
        }
        setSubscriber(res?.data);
      }

      async function postNewsLetterSubsriber() {
        const res = await PostNewsLetterSubscriber({facility_id: clientPortalStore?.selectedFacility?.id}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error subscribing to newsletter")));
            return;
        }
        void closeSubscribeModal();
        dispatch(showSuccess(t("Succesfully subscribed to newsletter")));
        setSubscriber(res?.data?.data as INewsLetterSubscriber);
      }

      async function unsubscribeNewsLetterSubsriber() {
        const res = await PutNewsLetterSubscriber({facility_id: clientPortalStore?.selectedFacility?.id, subscribed: false}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error subscribing to newsletter")));
            return;
        }
        void closeUnsubscribeModal();
        dispatch(showSuccess(t("Succesfully unsubscribed from newsletter")));
        setSubscriber(null);
      }

      const facilities = useMemo(() => {
        if (state?.facilitySearchQuery) {
          return clientPortalStore?.client?.facilities?.filter(
            facility =>
              facility?.long_name.toLowerCase().includes(state?.facilitySearchQuery?.toLowerCase()) ||
              facility?.city.toLowerCase().includes(state?.facilitySearchQuery?.toLowerCase()),
          );
        } else if (clientPortalStore?.client?.facilities) {
          return clientPortalStore?.client?.facilities;
        } else {
          return [];
        }
      }, [state?.searchedFacilities, clientPortalStore?.client?.facilities, state?.facilitySearchQuery]);

      function handleFacilityChange(id: number) {
        const foundFacility = clientPortalStore?.client?.facilities?.find(facility => facility?.id === id);
        if (foundFacility) {
            dispatch(clientPortalUpdate({ selectedFacility: foundFacility }));
        }
      }

      const selectedFacilityElement = (
        <div className="newsletter-facility-container">
          {clientPortalStore?.client?.facilities?.length > 1 && clientPortalStore?.selectedFacility?.logo_source && (
            <div>
              <img className="newsletter-home-course-logo" src={clientPortalStore?.selectedFacility?.logo_source} />
            </div>
          )}
          <div>
            <div className="newsletter-home-course-name">{clientPortalStore?.selectedFacility?.long_name}</div>
            {clientPortalStore?.selectedFacility && (
              <div className="newsletter-course-secondary-information">
                {`${clientPortalStore?.selectedFacility?.address_line_1}, ${clientPortalStore?.selectedFacility?.city}, ${clientPortalStore?.selectedFacility?.province_name}, ${clientPortalStore?.selectedFacility?.postal}`}
              </div>
            )}
            {clientPortalStore?.selectedFacility?.address_line_2 && (
              <div className="newsletter-course-secondary-information">
                {clientPortalStore?.selectedFacility?.address_line_2}
              </div>
            )}
          </div>
        </div>
      );

      async function logoutUser() {
        const logoutRes = await UserLogout(true);
        if (logoutRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error logging out")));
        }
        window.location.reload();
      }

    
    return (
        <div className="client-portal-newsletter">
        <ClientPortalNavigation showNavOptions />
        <div className="newsletter-options-container">
        {clientPortalStore?.client?.facilities?.length > 1 ? (
          <CustomDropdown
            parentElement={selectedFacilityElement}
            onChange={handleFacilityChange}
            defaultValue={clientPortalStore?.selectedFacility?.id}
            onSearch={searchString => setState(prevState => ({ ...prevState, facilitySearchQuery: searchString }))}
            searchPlacerHolder={t("Search courses...")}
          >
            {facilities ? (
              facilities?.map((facility, index) => {
                return (
                  <CustomDropdownOption value={facility.id} key={index}>
                    <div className="newsletter-facility-option-container">
                      <img className="newsletter-facility-option-facility-logo" src={facility?.logo_source} />
                      <p className="newsletter-facility-option-facility-name">{facility?.long_name}</p>
                      <p className="newsletter-facility-option-facility-address">{facility?.city}</p>
                      {facility?.favourite && (
                        <FontAwesomeIcon
                          size="1x"
                          fixedWidth
                          className="newsletter-facility-option-favourite"
                          icon={faStar}
                        />
                      )}
                    </div>
                  </CustomDropdownOption>
                );
              })
            ) : (
              <div className="facility-spinner">
                <Spin />
              </div>
            )}
          </CustomDropdown>
        ) : (
          selectedFacilityElement
        )}
      </div>
      {selectedPost ? (
        <div className="client-portal-newsletter-container">
          <div className="newsletter-selected-post-header">
              <p className="newsletter-breadcrumbs" onClick={() => setSelectedPost(null)}>{t("Newsletter")}</p>
              <FontAwesomeIcon icon={faChevronRight} color="#D0D5DD"/>
              <p className="newsletter-breadcrumbs-selected">{selectedPost?.title}</p>
          </div>
          <div className="newsletter-selected-container">
            <div className="flex flex-col gap-1">
              <p className="newsletter-publish-date">{moment(selectedPost?.published_at, "YYYY-MM-DD HH:mm:ss")?.format("ll")}</p>
              <h1 className="newsletter-header-text">{selectedPost?.title}</h1>
              <p className="newsletter-content newsletter-content-expanded"><Markdown markdownText={selectedPost?.content}/></p>
            </div>
            <div className="client-portal-newsletter-footer">
              <div>
                <Button
                  size="small"
                  type="secondary"
                  onClick={() => setSelectedPost(() => {
                    const foundPostIndex = posts?.findIndex(post => post?.id === selectedPost?.id);
                    if (foundPostIndex !== -1) {
                      return posts[foundPostIndex - 1];
                    }
                  })}
                  disabled={posts?.findIndex(post => post?.id === selectedPost?.id) === 0}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  &nbsp;
                  {t("Prev")}
                </Button>
              </div>
              <div>
                <Button
                  size="small"
                  type="secondary"
                  onClick={() => setSelectedPost(() => {
                    const foundPostIndex = posts?.findIndex(post => post?.id === selectedPost?.id);
                    if (foundPostIndex !== -1) {
                      return posts[foundPostIndex + 1];
                    }
                  })}
                  disabled={posts?.findIndex(post => post?.id === selectedPost?.id) === posts?.length - 1}
                >
                  {t("Next")}
                  &nbsp;
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : <>
        <div className="newsletter-options-container">
        <div>
          <p className="client-portal-newsletter-title">{t("Newsletter")}</p>
          <p className="client-portal-newsletter-subtitle">{t("Subscribe to get the latest news and updates of {{facilityName}}", {facilityName: clientPortalStore?.selectedFacility?.long_name})}</p>
        </div>
        {clientPortalStore?.user ? (
          <div className="client-portal-newsletter-button-group">
            {subscriber?.subscribed ? (
              <Button type="error-outline" size="medium" onClick={() => updateUnsubscribeModal({isOpen: true})} loading={subscriber === undefined}>{t("Unsubscribe")}</Button>
            ) : null}
            <Button type="primary" size="medium" onClick={() => updateSubscribeModal({isOpen: true})} loading={subscriber === undefined} disabled={subscriber?.subscribed}>{subscriber?.subscribed ? <><FontAwesomeIcon icon={faCheck} className="mr-1"/> {t("Subscribed")}</> : t("Subscribe")}</Button>
          </div>
        ) : null}
      </div>
        <div className="client-portal-newsletter-container">
            <div className="client-portal-newsletter-list-container">
              {posts === undefined ? (
                <span style={{ display: "flex", justifyContent: "center" }}>
                <Spin />
              </span>
              ) : posts?.length === 0 ? (
                <MessageDisplay icon={"circle-minus"} title={t("No newsletter available")} content={t("This facility hasn't published any newsletters yet. Check back later for updates.")}/>
              ) : posts?.map((post, index) => {
                return (
                  <div key={index} className="news-letter-card" onClick={() => setSelectedPost(post)}>
                    <div style={{width: windowSize?.width > MOBILE_WIDTH ? "80%" : undefined}} className="flex flex-col gap-1">
                      <p className="newsletter-publish-date">{moment(post?.published_at, "YYYY-MM-DD HH:mm:ss")?.format("ll")}</p>
                      <h1 className="newsletter-header-text">{post?.title}</h1>
                      <p className="newsletter-content">{post?.content?.substring(0, windowSize?.width > MOBILE_WIDTH ? 200 : 100)}</p>
                    </div>
                    
                    {windowSize?.width > MOBILE_WIDTH ? (
                      <Button type="secondary" size="medium">{t("Read More")} <FontAwesomeIcon icon={faChevronRight}/></Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="client-portal-newsletter-footer">
              <div>
                <Button
                  size="small"
                  type="secondary"
                  onClick={() => setOffset(prev => prev - TABLE_LIMIT )}
                  disabled={!posts || posts?.length === 0 || !(TABLE_LIMIT <= offset)}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  &nbsp;
                  {t("Prev")}
                </Button>
              </div>
              <div>
                <Button
                  size="small"
                  type="secondary"
                  onClick={() => setOffset(prev => prev + TABLE_LIMIT )}
                  disabled={!posts || posts?.length === 0 || !(posts?.length === TABLE_LIMIT)}
                >
                  {t("Next")}
                  &nbsp;
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </div>
            </div>
        </div>
      </>}
      
        


        <Portal isMounted={subscribeModal?.isOpen}>
            <Popup open={subscribeModal?.isOpen} type={"info"} okText={t("Subscribe")} title={t("Subscribe")} description={t("You'll receive newsletter emails from {{facilityName}}. You can unsubscribe anytime", {facilityName: clientPortalStore?.selectedFacility?.long_name})} closable onCancel={closeSubscribeModal} onOk={postNewsLetterSubsriber}/>
        </Portal>

        <Portal isMounted={unsubscribeModal?.isOpen}>
            <Popup open={unsubscribeModal?.isOpen} type={"warning"} okText={t("Unsubscribe")} title={t("Unsubscribe")} description={t("You'll stop receiving newsletter emails from {{facilityName}} once unsubscribed", {facilityName: clientPortalStore?.selectedFacility?.long_name})} closable onCancel={closeUnsubscribeModal} onOk={unsubscribeNewsLetterSubsriber}/>
        </Portal>

      <Portal isMounted={adminUserLoggedIn}>  
      <Popup
        open={adminUserLoggedIn}
        type="warning"
        title={t("Admin Account Detected")}
        description={t("Looks like you are logged into an admin account. Click logout below to view the tee times")}
        onOk={logoutUser}
        okText={t("Logout")}
      />
      </Portal>
        
      </div>
    );
}